import { Component, Input, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, forwardRef } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { IDpOptions, IDpDateModel } from '@m27/common/lib/modules/date-picker';
import { UserAccess } from '@core/authorization/user-access';
import { Globals } from '@shared/models/globals';
import { GridOptions, ColDef, ColGroupDef, RowNode, SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { DialogBase, M27Dialog, DataGrid, DialogButton, Dialogs, M27TextBoxConfig, M27InputType, M27DatePickerConfig, M27HorizontalAlignment, M27InputComponent } from '@m27/common';
import { PermitService } from '@shared/services';
import { ReportService } from '@reporting/services';
import { environment } from '@app/environment';
import { DataGridColumns } from '@shared/models';
import { IPlantingReportCriteria } from '@reporting/interfaces';
import { IPermitSearch, IPermit } from '@shared/interfaces';
import { ICheckBox } from '@shared/interfaces/form-controls';

@Component({
    selector: 'reporting-dialog-planting-report-criteria',
    templateUrl: './dialog-planting-report-criteria.component.html',
    styleUrls: ['./dialog-planting-report-criteria.component.scss'],
    providers: [{ provide: DialogBase, useExisting: forwardRef(() => PlantingReportCriteriaDialogComponent) }]
})
export class PlantingReportCriteriaDialogComponent extends DialogBase implements M27Dialog {
    public gridOptions: GridOptions;
    public totalRowCount = 0;
    public userAccess: UserAccess = Globals.UAC;
    public selectedRowCount = 0;
    public hasSearched = false;
    public isSearching = false;
    public isDateRangeValid = true;
    public hasValidFromDate = false;

    @ViewChild('dateRangeTo', { static: true }) dateRangeTo: M27InputComponent;

    public dateRangeFromConfig: M27DatePickerConfig = {
        InputType: M27InputType.DatePicker,
        InputMask: {
            Mask: '00/00/0000',
            BackgroundText: 'MM/DD/YYYY'
        },
        OnChange: (value: Date) => {
            this.hasValidFromDate = value !== null && String(value) !== 'Invalid date';

            if (this.hasValidFromDate) {
                this.dateRangeToConfig.MinDate = value;

                this.dateRangeTo.focus();
                this.dateRangeTo.open();
            }
        }
    };

    public dateRangeToConfig: M27DatePickerConfig = {
        InputType: M27InputType.DatePicker,
        InputMask: {
            Mask: '00/00/0000',
            BackgroundText: 'MM/DD/YYYY'
        }
    };

    public minusDaysConfig: M27TextBoxConfig = {
        TextAlignment: M27HorizontalAlignment.Right,
        InputType: M27InputType.Textbox
    };

    public fromPlantDate: Date;
    public toPlantDate: Date;
    public minusDays = 240;
    public showPlantDatesNotReported = true;
    public showUpdatedPlantingInfo = true;
    public showLocsWithConstructs = true;
    public availableCrops: ICheckBox[] = [
        { Name: 'Corn', Value: 'CRN', IsChecked: false, IsDisabled: false },
        { Name: 'Soybeans', Value: 'SOY', IsChecked: false, IsDisabled: false },
        { Name: 'Sorghum', Value: 'SORG', IsChecked: false, IsDisabled: false },
        { Name: 'Canola', Value: 'CANO', IsChecked: false, IsDisabled: false },
        { Name: 'Wheat', Value: 'WHE', IsChecked: false, IsDisabled: false },
        { Name: 'Cotton', Value: 'COT', IsChecked: false, IsDisabled: false }
    ];
    private numberOfYears = environment.env === 'PROD' ? 5 : 10;
    public availableYears: ICheckBox[] = [];
    public availableStatus: ICheckBox[] = [
        { Name: 'Valid', Value: 'VLD', IsChecked: false, IsDisabled: false },
        { Name: 'Expired', Value: 'EXP', IsChecked: false, IsDisabled: false }
    ];

    public reportIdJustCreated = 0;
    public showReportJustCreatedBadge = false;
    private showNewReportBadgeSubscription: Subscription;

    // this is only calls once for entire web session
    constructor(private permitService: PermitService, private reportService: ReportService, cd: ChangeDetectorRef, elRef: ElementRef) {
        super(cd, elRef);

        const startYear: number = (new Date()).getFullYear();

        for (let x = 0; x < this.numberOfYears; x++) {
            this.availableYears.push({ Name: `${startYear - x}`, Value: startYear - x, IsChecked: false, IsDisabled: false });
        }
    }

    // this is only calls once for entire web session
    public onDialogReady(): void {
        this.setupDataGrid();
    }

    private setupDataGrid(): void {
        this.gridOptions = DataGrid.GetDefaults(this, 'PLANTING-REPORT-PERMITS', () => {}, 'PermitId');

        this.gridOptions.columnDefs = [
            DataGrid.ColumnBuilder()
                .ForRowCheckBox()
                .Build(),

            ...DataGridColumns.PermitSearchResults()
        ] as (ColDef | ColGroupDef)[];

        this.gridOptions.onSelectionChanged = (event: SelectionChangedEvent) => {
            this.selectedRowCount = this.gridOptions.api.getSelectedRows().filter((row: RowNode) => {
                return !row.group;
            }).length;
        };
    }

    private setupButtons(): void {
        if (this.isBackButtonEnabled()) {
            this.leftButtons.push(new DialogButton({
                IsCancel: true,
                Text: 'Back',
                Action: (dialogBase: DialogBase, isChecked?: boolean) => {
                    this.close();
                }
            } as DialogButton));
        }

        this.rightButtons.push(new DialogButton({
            IsCancel: false,
            Text: 'Run Report (Stay)',
            IsDisabled: () => {
                return !this.validateCanRunReport();
            },
            Action: (dialogBase: DialogBase, isChecked?: boolean) => {
                this.runReport(true);
            }
        } as DialogButton));

        this.rightButtons.push(new DialogButton({
            IsCancel: false,
            Text: 'Run Report (Go)',
            IsDisabled: () => {
                return !this.validateCanRunReport();
            },
            Action: (dialogBase: DialogBase, isChecked?: boolean) => {
                this.runReport(false);
            }
        } as DialogButton));
    }

    private runReport(stay: boolean): void {
        this.reportService.RunPlantingReport({
            FromDate: this.fromPlantDate,
            ToDate: this.toPlantDate,
            IncludeLocationsWithConstructs: this.showLocsWithConstructs,
            IncludePlantDatesNotReported: this.showPlantDatesNotReported,
            IncludePlantDatesReported: this.showUpdatedPlantingInfo,
            PermitIds: this.gridOptions.api.getSelectedNodes().map(row => row.data.PermitId),
            MinusDays: this.minusDays
        } as IPlantingReportCriteria).subscribe((reportId: number) => {
            if (!stay) {
                Dialogs.navigate(`/reporting/reports/planting/${reportId}`);
                return;
            }

            this.reportIdJustCreated = reportId;
            this.showReportJustCreatedBadge = true;

            if (this.showNewReportBadgeSubscription !== undefined) {
                this.showNewReportBadgeSubscription.unsubscribe();
            }

            this.showNewReportBadgeSubscription = timer(3500).subscribe(() => {
                this.showReportJustCreatedBadge = false;
            });
        });
    }

    public validateCanSearchForPermits(): boolean {
        const hasCrop = this.availableCrops.filter(item => item.IsChecked).length > 0;
        const hasYear = this.availableYears.filter(item => item.IsChecked).length > 0;
        const hasStatus = this.availableStatus.filter(item => item.IsChecked).length > 0;

        return hasCrop && hasYear && hasStatus;
    }

    public validateCanRunReport(): boolean {
        return this.validateDates(false) && this.selectedRowCount > 0;
    }

    public validateDates(canBeBlank?: boolean): boolean {
        if (canBeBlank === undefined) {
            canBeBlank = true;
        }

        if (canBeBlank) {
            if (this.fromPlantDate === null || this.fromPlantDate === undefined || this.toPlantDate === null || this.toPlantDate === undefined) {
                return true;
            }
        }
        else if (this.fromPlantDate === null || this.fromPlantDate === undefined || this.toPlantDate === null || this.toPlantDate === undefined) {
            return false;
        }

        if (this.fromPlantDate <= this.toPlantDate) {
            return true;
        }
        else {
            return false;
        }
    }

    public searchForPermits(): void {
        this.isSearching = true;
        this.totalRowCount = 0;
        this.gridOptions.api.setRowData([]);

        DataGrid.ShowLoading(this.gridOptions, 'Searching...');

        this.permitService.Search({
            Crops: this.availableCrops.filter(item => item.IsChecked).map(item => item.Value),
            Years: this.availableYears.filter(item => item.IsChecked).map(item => item.Value),
            Statuses: this.availableStatus.filter(item => item.IsChecked).map(item => item.Value),
            Countries: [539230],
            Types: ['FUFR', 'FURM', 'NFR', 'NFRM']
        } as IPermitSearch).subscribe((results: IPermit[]) => {
            this.hasSearched = true;
            this.isSearching = false;
            this.totalRowCount = results.length;
            this.gridOptions.api.setRowData(results);

            if (this.totalRowCount === 0) {
                DataGrid.ShowLoading(this.gridOptions, 'No Permits Found', true);
            }
            else {
                DataGrid.HideLoading(this.gridOptions);
            }
        });
    }

    public open(closeAll: boolean = false) {
        this.onOpen.emit(false);
        this.modal.open();

        this.totalRowCount = 0;
        this.hasSearched = false;
        this.setupButtons();
    }

    onDestroy() {
        this.clearButtons();
    }

    canCancel(): boolean {
        return true;
    }
}
